// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { lazy, Suspense, Component } from "react"
import type { ReactNode } from "react"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { AuthProvider, useAuth } from "./context/AuthContext"
import { LanguageProvider } from "./i18n/LanguageContext"
import Layout from "./components/Layout"
import CookieBanner from "./components/CookieBanner"

// Public pages — always in main bundle
import Landing from "./pages/Landing"
import Login from "./pages/Login"
import Signup from "./pages/Signup"
import Privacy from "./pages/Privacy"
import Terms from "./pages/Terms"
import Cookies from "./pages/Cookies"
import Support from "./pages/Support"
import Beta from "./pages/Beta"

// All app pages — lazy loaded (separate chunks, only fetched on navigation)
const Dashboard              = lazy(() => import("./pages/Dashboard"))
const Logs                   = lazy(() => import("./pages/Logs"))
const Violations             = lazy(() => import("./pages/Violations"))
const Policies               = lazy(() => import("./pages/Policies"))
const PolicyBuilder          = lazy(() => import("./pages/PolicyBuilder"))
const ComplianceHub          = lazy(() => import("./pages/ComplianceHub"))
const RegulatoryIntelligence = lazy(() => import("./pages/RegulatoryIntelligence"))
const Settings               = lazy(() => import("./pages/Settings"))
const Check                  = lazy(() => import("./pages/Check"))
const Docs                   = lazy(() => import("./pages/Docs"))
const Demo                   = lazy(() => import("./pages/Demo"))
const Pricing                = lazy(() => import("./pages/Pricing"))
const Playbook               = lazy(() => import("./pages/Playbook"))
const Pitch                  = lazy(() => import("./pages/Pitch"))
const Agents                 = lazy(() => import("./pages/Agents"))
const Handoff                = lazy(() => import("./pages/Handoff"))
const EmployeeBook           = lazy(() => import("./pages/EmployeeBook"))
const InviteTeam             = lazy(() => import("./pages/InviteTeam"))
const Onboarding             = lazy(() => import("./pages/Onboarding"))
const PostBeta               = lazy(() => import("./pages/PostBeta"))
const Wiki                   = lazy(() => import("./pages/Wiki"))
const WikiView               = lazy(() => import("./pages/WikiView"))
const WikiEditor             = lazy(() => import("./pages/WikiEditor"))
const Compose                = lazy(() => import("./pages/Compose"))
const Team                   = lazy(() => import("./pages/Team"))
const TeamProfile            = lazy(() => import("./pages/TeamProfile"))

// Redirects free-plan users to the upgrade page when they hit a paid route directly
function PaidRoute({ children }: { children: React.ReactNode }) {
  const { isPaid, isInternal, loading } = useAuth()
  if (loading) return null
  if (!isPaid && !isInternal) return <Navigate to="/settings?upgrade=1" replace />
  return <>{children}</>
}

// Blocks non-internal users from seeing internal team pages
function InternalRoute({ children }: { children: React.ReactNode }) {
  const { isInternal, loading } = useAuth()
  if (loading) return null
  if (!isInternal) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

// Exec-only: internal + admin role — strategy docs, agent console, wiki, employee book
function ExecRoute({ children }: { children: React.ReactNode }) {
  const { isInternal, profile, loading } = useAuth()
  if (loading) return null
  if (!isInternal || profile?.role !== "admin") return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

// Redirects users without the Policy Builder add-on to settings
function AddonRoute({ children }: { children: React.ReactNode }) {
  const { isPaid, isInternal, hasPolicyBuilder, loading } = useAuth()
  if (loading) return null
  if (!isPaid && !isInternal) return <Navigate to="/settings?upgrade=1" replace />
  if (!hasPolicyBuilder && !isInternal) return <Navigate to="/settings?addon=policy-builder" replace />
  return <>{children}</>
}

function ProtectedRoutes() {
  const { session, loading } = useAuth()
  if (loading) return <div style={{minHeight:"100vh",background:"#030712"}} className="flex items-center justify-center"><div className="text-gray-500 text-sm">Loading...</div></div>
  if (!session) return <Navigate to="/login" replace />
  return (
    <Layout>
      <Routes>
        {/* Free tier routes — always accessible */}
        <Route path="/"           element={<Dashboard />} />
        <Route path="/dashboard"  element={<Dashboard />} />
        <Route path="/check"      element={<Check />} />
        <Route path="/settings"   element={<Settings />} />
        <Route path="/pricing"    element={<Pricing />} />
        <Route path="/docs"       element={<PaidRoute><Docs /></PaidRoute>} />
        <Route path="/playbook"   element={<PaidRoute><Playbook /></PaidRoute>} />
        <Route path="/pitch"      element={<PaidRoute><Pitch /></PaidRoute>} />
        <Route path="/demo"       element={<PaidRoute><Demo /></PaidRoute>} />

        {/* Paid tier routes — redirect free users to upgrade */}
        <Route path="/logs"           element={<PaidRoute><Logs /></PaidRoute>} />
        <Route path="/violations"     element={<PaidRoute><Violations /></PaidRoute>} />
        <Route path="/policies"       element={<PaidRoute><Policies /></PaidRoute>} />
        <Route path="/policy-builder" element={<AddonRoute><PolicyBuilder /></AddonRoute>} />
        <Route path="/compliance-hub"          element={<PaidRoute><ComplianceHub /></PaidRoute>} />
        <Route path="/regulatory-intelligence" element={<PaidRoute><RegulatoryIntelligence /></PaidRoute>} />

        {/* Internal team routes — is_internal=true only */}
        <Route path="/invite"        element={<InternalRoute><InviteTeam /></InternalRoute>} />
        <Route path="/onboarding"    element={<InternalRoute><Onboarding /></InternalRoute>} />

        {/* Exec-only routes — is_internal=true AND role=admin (strategy, agents, wiki, employee book) */}
        <Route path="/compose"         element={<ExecRoute><Compose /></ExecRoute>} />
        <Route path="/agents"          element={<ExecRoute><Agents /></ExecRoute>} />
        <Route path="/handoff"         element={<ExecRoute><Handoff /></ExecRoute>} />
        <Route path="/employee-book"   element={<ExecRoute><EmployeeBook /></ExecRoute>} />
        <Route path="/post-beta"       element={<ExecRoute><PostBeta /></ExecRoute>} />
        <Route path="/wiki"            element={<ExecRoute><Wiki /></ExecRoute>} />
        <Route path="/wiki/new"        element={<ExecRoute><WikiEditor /></ExecRoute>} />
        <Route path="/wiki/:slug/edit" element={<ExecRoute><WikiEditor /></ExecRoute>} />
        <Route path="/wiki/:slug"      element={<ExecRoute><WikiView /></ExecRoute>} />
        <Route path="/team"            element={<ExecRoute><Team /></ExecRoute>} />
        <Route path="/team/:id"        element={<ExecRoute><TeamProfile /></ExecRoute>} />
      </Routes>
    </Layout>
  )
}

// / → Landing for guests, Dashboard for signed-in users
function HomeRoute() {
  const { session, loading } = useAuth()
  if (loading) return <PageLoader />
  if (session) return <Navigate to="/dashboard" replace />
  return <Landing />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth()
  if (loading) return null
  if (session) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

const PageLoader = () => <div style={{minHeight:"100vh",background:"#030712"}} className="flex items-center justify-center"><div className="text-gray-500 text-sm">Loading...</div></div>

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }
  static getDerivedStateFromError() { return { hasError: true } }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", background: "#030712" }} className="flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-sm font-semibold mb-2">Something went wrong</div>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.reload() }}
              className="text-xs text-gray-400 hover:text-gray-300 underline"
            >
              Reload page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <ErrorBoundary>
    <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <CookieBanner />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/"        element={<HomeRoute />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/login"   element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/signup"  element={<PublicRoute><Signup /></PublicRoute>} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms"   element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/support" element={<Support />} />
          <Route path="/beta"    element={<Beta />} />
          <Route path="/*"       element={<ProtectedRoutes />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
    </ErrorBoundary>
  )
}
